import React from 'react';
import { convertToUserUnit, formatDisplayValue, calculateBMIWithUnits } from '../utils/unitConverter';
import useUnitPreferences from '../hooks/useUnitPreferences';

/**
 * Reusable measurement display component
 * Automatically converts and displays measurements based on user preferences
 */
const MeasurementDisplay = ({ 
  measurementType, 
  value, 
  originalUnit, 
  label, 
  showLabel = true,
  className = '',
  precision = 1,
  fallbackValue = 'N/A'
}) => {
  const { getPreferredUnitSystem } = useUnitPreferences();
  
  if (!value) {
    return (
      <div className={className}>
        {showLabel && <div className="small text-muted">{label}</div>}
        <div className="fw-bold">{fallbackValue}</div>
      </div>
    );
  }

  const converted = convertToUserUnit(value, originalUnit, getPreferredUnitSystem(), measurementType);
  
  if (!converted.value || isNaN(converted.value)) {
    return (
      <div className={className}>
        {showLabel && <div className="small text-muted">{label}</div>}
        <div className="fw-bold">{fallbackValue}</div>
      </div>
    );
  }

  return (
    <div className={className}>
      {showLabel && <div className="small text-muted">{label}</div>}
      <div className="fw-bold">
        {converted.formattedValue || `${parseFloat(converted.value.toFixed(precision))} ${converted.unit}`}
      </div>
    </div>
  );
};

/**
 * Component for displaying BMI with automatic unit conversion
 */
const BMIDisplay = ({ weight, weightUnit, height, heightUnit, className = '' }) => {
  const { getPreferredUnitSystem } = useUnitPreferences();
  
  const bmiResult = calculateBMIWithUnits(weight, weightUnit, height, heightUnit);
  
  if (!bmiResult.bmi) {
    return (
      <div className={className}>
        <div className="small text-muted">BMI</div>
        <div className="fw-bold">N/A</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="small text-muted">BMI</div>
      <div className="fw-bold">
        {bmiResult.bmi} <small className="text-muted">({bmiResult.category})</small>
      </div>
    </div>
  );
};

/**
 * Component for displaying vital signs with unit conversion
 */
const VitalSignsDisplay = ({ 
  systolic, 
  diastolic, 
  heartRate, 
  bloodGlucose, 
  bloodGlucoseUnit, 
  oxygenSaturation, 
  className = '' 
}) => {
  const { getPreferredUnitSystem } = useUnitPreferences();

  return (
    <div className={className}>
      <h6 className="fw-bold mb-3 text-primary">
        <i className="fas fa-heartbeat me-2"></i>
        Vital Signs
      </h6>
      
      <div className="row">
        {(systolic || diastolic) && (
          <div className="col-6 mb-2">
            <div className="small text-muted">Blood Pressure</div>
            <div className="fw-bold">
              {systolic ? systolic : 'N/A'}/{diastolic ? diastolic : 'N/A'} <small className="text-muted">mmHg</small>
            </div>
          </div>
        )}
        
        {heartRate && (
          <div className="col-6 mb-2">
            <div className="small text-muted">Heart Rate</div>
            <div className="fw-bold">{heartRate} <small className="text-muted">bpm</small></div>
          </div>
        )}
        
        {bloodGlucose && (
          <div className="col-6 mb-2">
            <MeasurementDisplay
              measurementType="blood_glucose"
              value={bloodGlucose}
              originalUnit={bloodGlucoseUnit}
              label="Blood Glucose"
              showLabel={false}
            />
          </div>
        )}
        
        {oxygenSaturation && (
          <div className="col-6 mb-2">
            <div className="small text-muted">Oxygen Saturation</div>
            <div className="fw-bold">{oxygenSaturation}%</div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Component for displaying physical measurements with unit conversion
 */
const PhysicalMeasurementsDisplay = ({ 
  weight, 
  weightUnit, 
  height, 
  heightUnit, 
  waist, 
  waistUnit, 
  hip, 
  hipUnit,
  className = '' 
}) => {
  return (
    <div className={className}>
      <h6 className="fw-bold mb-3 text-primary">
        <i className="fas fa-ruler me-2"></i>
        Physical Measurements
      </h6>
      
      <div className="row">
        {weight && (
          <div className="col-6 mb-2">
            <MeasurementDisplay
              measurementType="weight"
              value={weight}
              originalUnit={weightUnit}
              label="Weight"
              showLabel={false}
            />
          </div>
        )}
        
        {height && (
          <div className="col-6 mb-2">
            <MeasurementDisplay
              measurementType="height"
              value={height}
              originalUnit={heightUnit}
              label="Height"
              showLabel={false}
            />
          </div>
        )}
        
        {weight && height && (
          <div className="col-6 mb-2">
            <BMIDisplay 
              weight={weight} 
              weightUnit={weightUnit} 
              height={height} 
              heightUnit={heightUnit}
            />
          </div>
        )}
        
        {waist && (
          <div className="col-6 mb-2">
            <MeasurementDisplay
              measurementType="circumference"
              value={waist}
              originalUnit={waistUnit}
              label="Waist"
              showLabel={false}
            />
          </div>
        )}
        
        {hip && (
          <div className="col-6 mb-2">
            <MeasurementDisplay
              measurementType="circumference"
              value={hip}
              originalUnit={hipUnit}
              label="Hip"
              showLabel={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Complete patient measurements display component
 */
const PatientMeasurementsDisplay = ({ 
  patientData,
  showVitalSigns = true,
  showPhysicalMeasurements = true,
  className = ''
}) => {
  if (!patientData) {
    return <div className={className}>No patient data available</div>;
  }

  return (
    <div className={className}>
      {showPhysicalMeasurements && (
        <PhysicalMeasurementsDisplay
          weight={patientData.weight}
          weightUnit={patientData.weightUnit || patientData.weight_unit}
          height={patientData.height}
          heightUnit={patientData.heightUnit || patientData.height_unit}
          waist={patientData.waist}
          waistUnit={patientData.waistUnit || patientData.waist_unit}
          hip={patientData.hip}
          hipUnit={patientData.hipUnit || patientData.hip_unit}
        />
      )}
      
      {showVitalSigns && (
        <div className="mt-4">
          <VitalSignsDisplay
            systolic={patientData.systolicBloodPressure}
            diastolic={patientData.diastolicBloodPressure}
            heartRate={patientData.restingHeartRate}
            bloodGlucose={patientData.bloodGlucose}
            bloodGlucoseUnit={patientData.bloodGlucoseUnit || patientData.blood_glucose_unit}
            oxygenSaturation={patientData.oxygenSaturationLevel}
          />
        </div>
      )}
    </div>
  );
};

export default MeasurementDisplay;
export { BMIDisplay, VitalSignsDisplay, PhysicalMeasurementsDisplay, PatientMeasurementsDisplay };